import {
	Box,
	HStack,
	useColorModeValue as mode,
	Text,
	Button,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	useDisclosure,
	useToast,
	Image
} from "@chakra-ui/react";
import {
	GiSwordsEmblem,
	GiHerbsBundle,
} from "react-icons/gi";
import { BiCheck, BiX } from "react-icons/bi";
import { useState, useEffect, ReactNode } from "react";
import {
	areRaidersApproved,
	approveRaidersForQuesting,
	startQuest,
	minExpForQuest,
	getRaiderSpeedBoost,
} from "../../../util/web3/interactQuests";
import { levelCalculator } from "../../../util/helpers";
import { getRaiderExp } from "../../../util/web3/interactProfessions";
import { Raider } from "../../../types/raider";

interface DescriptionProps {
	title: string;
	children: ReactNode;
	location: string;
	image: string;
	comingSoon?: boolean;
	level: string;
	raider: Raider;
	rewards: string;
	experience: string;
	icon: string;
}

export const QuestDescription = (props: DescriptionProps) => {
	const { title, children, comingSoon, image, raider, level, experience, icon } =
		props;
	const [minExp, setMinExp] = useState<number>(0);
	const [raiderExp, setRaiderExp] = useState<number>(0);
	const [raiderSpeed, setRaiderSpeed] = useState<number>(100);
	const startDungeonModal = useDisclosure();

	const toast = useToast();

	const questHandler = async (raider: Raider, level: string) => {
		if (!process.env.NEXT_PUBLIC_QUESTING) {
			throw "missing questing env.";
		}
		const approval = await areRaidersApproved(process.env.NEXT_PUBLIC_QUESTING);

		if (!approval) {
			await approveRaidersForQuesting(process.env.NEXT_PUBLIC_QUESTING, toast);
		} else if (approval) {
			startQuest(Number(raider.id), level, toast);
			startDungeonModal.onClose();
		}
	};

	const checkOrX = () => {
		if (raiderExp >= minExp) {
			return <Box as={BiCheck} fontSize="md" color="gray.400" />;
		} else {
			return <Box as={BiX} fontSize="md" color="gray.400" />;
		}
	};

	const levelCheck = () => {
		if (raiderExp >= minExp) {
			startDungeonModal.onOpen();
		} else {
			toast({
				title: "Insufficient Level!",
				description: `This quest requires ${levelCalculator(
					minExp
				)} ${experience}, but you are only level ${levelCalculator(
					raiderExp
				)}. Try an easier quest!`,
				status: "error",
				duration: 9000,
				position: "top",
				isClosable: true,
			});
		}
	};

	useEffect(() => {
		const professionResolver = (expName: string) => {
			switch (expName) {
				case "Herbalism":
					return 1;
				default:
					throw `invalid profession - ${expName}`;
			}
		};

		const fetchData = async () => {
			const minResponse = await minExpForQuest(level);
			if (minResponse != undefined) {
				setMinExp(minResponse);
			}
			const raiderXp = await getRaiderExp(
				Number(raider.id),
				professionResolver(experience)
			);
			if (raiderXp != undefined) {
				setRaiderExp(raiderXp);
			}
			const tempRaiderSpeed = await getRaiderSpeedBoost(Number(raider.id));
			if (tempRaiderSpeed) {
				setRaiderSpeed(tempRaiderSpeed);
			}
		};

		fetchData();
	}, []);

	return (
		<Box
			position="relative"
			background="#4a556878"
			borderRadius="md"
			px={4}
			py={4}
		>
			<HStack spacing={4}>
				<Box className={image}></Box>
				<Box>
					<Box fontWeight="bold" fontSize={18} maxW="xl" mb="2">
						{title}
					</Box>
					<HStack
						fontSize="sm"
						fontWeight="medium"
						color={mode("gray.500", "white")}
						mt="1"
					>
						<Image 
							src={icon}
							width="20px"
							height="20px"
							alt={experience}
						/>
						<span>{props.rewards}</span>
					</HStack>
					<HStack
						fontSize="sm"
						fontWeight="medium"
						color={mode("gray.500", "white")}
						mt="1"
					>
						{checkOrX()}
						{/* <span>{levelCalculator(minExp)}</span> */}
						<span>
							Requires {experience} Level {levelCalculator(minExp)}
						</span>
					</HStack>
				</Box>
			</HStack>
			<Box mt="3" ml="5" mr="5" mb="3" color={mode("gray.600", "gray.200")}>
				{children}
			</Box>
			<HStack
				position={{ sm: "absolute" }}
				top={{ sm: "2" }}
				insetEnd={{ sm: "4" }}
				mt={{ base: "2", sm: "2" }}
				spacing={4}
			>
				{/* <IconButton aria-label="Edit" icon={<HiInformationCircle />} size="md"/> */}
				<Button
					rightIcon={<GiHerbsBundle />}
					colorScheme="green"
					isDisabled={comingSoon}
					onClick={() => levelCheck()}
				>
					{comingSoon ? "Coming Soon" : "Quest"}
				</Button>
			</HStack>
			<Modal
				isOpen={startDungeonModal.isOpen}
				onClose={startDungeonModal.onClose}
				size="lg"
			>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Start the Quest?</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Text mb={5}>
							You are about to begin <strong>[{props.title}] </strong>
							with {raider.name}.
						</Text>
						<Text mb={5}>
							They&apos;ll work on the quest on their own. The longer
							they&apos;re gone, the more <strong>{props.rewards}</strong>{" "}
							they&apos;ll return with and the more{" "}
							<strong>{experience}</strong> experience they&apos;ll receive.
						</Text>
						<Text mb={5}>
							You will not be able to run dungeons with them while they&apos;re
							questing, and it will take them time to return home after the
							quest.
						</Text>
						<Text>Your Raiders Speed: {raiderSpeed}%</Text>
						<Text>Your {experience} Boosts: None</Text>
						<Text>Your {experience} Level Boost: None</Text>
					</ModalBody>

					<ModalFooter>
						<Button
							colorScheme="red"
							variant="ghost"
							mr={3}
							onClick={startDungeonModal.onClose}
						>
							Nevermind
						</Button>
						<Button
							rightIcon={<GiSwordsEmblem />}
							colorScheme="green"
							onClick={() => questHandler(raider, level)}
						>
							Start
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Box>
	);
};
